import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { BottomNavigation } from '../components/BottomNavigation';
import { BackButton } from '../components/ui';
import { ProductReviewsDrawer } from '../components/ui/ProductReviewsDrawer';
import { ProductInfoDrawer } from '../components/ui/ProductInfoDrawer';
import { AvailableChip } from '../components/AvailableChip';
import { Heart, Buy, MoreCircle, ChevronLeft, Star } from 'react-iconly';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { doc as docFirestore, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getProductReviews } from '../lib/reviewsService';

console.log('[StoreProductDetail] File loaded successfully at:', new Date().toISOString());

const NAV_HEIGHT = 72; // px, adjust if your BottomNavigation is taller
const SAFE_AREA = 20; // px, safe area for mobile devices

// Function to render star rating
const renderStarRating = (rating) => {
  // Handle invalid rating values
  if (!rating || typeof rating !== 'number' || isNaN(rating)) {
    return <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>No rating</div>;
  }
  
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      {[...Array(fullStars)].map((_, i) => (
        <Star 
          key={`full-${i}`} 
          set="bold" 
          size={14} 
          primaryColor="#FFFFFF"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))' }}
        />
      ))}
      {hasHalfStar && (
        <Star 
          key="half" 
          set="light" 
          size={14} 
          primaryColor="#FFFFFF"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))' }}
        />
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star 
          key={`empty-${i}`} 
          set="light" 
          size={14} 
          primaryColor="#D1D5DB"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))' }}
        />
      ))}
    </div>
  );
};

const StoreProductDetail = () => {
  const { productId } = useParams();
  console.log('[StoreProductDetail] Component rendered with productId:', productId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addCartItem, cartItems, updateCartItem, removeCartItem } = useCart();
  const { currentUser } = useAuth();
  
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetProductIdx, setSheetProductIdx] = useState(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [variationSelections, setVariationSelections] = useState({});
  const [cartAnim, setCartAnim] = useState([]);
  
  // Reviews drawer state
  const [showReviewsDrawer, setShowReviewsDrawer] = useState(false);
  const [selectedProductForReviews, setSelectedProductForReviews] = useState(null);
  
  // Product info drawer state
  const [showProductInfoDrawer, setShowProductInfoDrawer] = useState(false);
  const [selectedProductForInfo, setSelectedProductForInfo] = useState(null);
  
  const videoRefs = useRef([]);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  // State for managing scroll behavior
  const [hasInitialScroll, setHasInitialScroll] = useState(false);
  const [isInitialPositioning, setIsInitialPositioning] = useState(false);

  // Handle viewport height for mobile browsers
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial viewport height
    setViewportHeight();

    // Update on resize
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [dragOverProductId, setDragOverProductId] = useState(null);
  const [reorderError, setReorderError] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [skipNextFetch, setSkipNextFetch] = useState(false);

  // Helper function to get cart quantity for a product
  const getCartQty = (productId) => {
    const cartItem = cartItems.find(item => item.id === productId);
    return cartItem ? cartItem.qty : 0;
  };

  // Check if product has variations that require selection
  const checkProductHasVariations = (product) => {
    return product.variants && product.variants.length > 0;
  };

  // Check if all variations are selected
  const allVariationsSelected = (product) => {
    if (!product.variants || product.variants.length === 0) return true;
    return product.variants.every(variant => 
      variationSelections[variant.key] !== undefined
    );
  };

  // Open bottom sheet
  const openSheet = (idx) => {
    setSheetProductIdx(idx);
    setShowSheet(true);
    setQuantity(1);
    setVariationSelections({});
  };

  // Close bottom sheet
  const closeSheet = () => {
    setShowSheet(false);
    setSheetProductIdx(null);
  };

  const handleOpenReviews = (product) => {
    setSelectedProductForReviews(product);
    setShowReviewsDrawer(true);
  };

  const handleCloseReviews = () => {
    setShowReviewsDrawer(false);
    setSelectedProductForReviews(null);
  };

  const handleOpenProductInfo = (product) => {
    console.log('[StoreProductDetail] Opening product info drawer for:', product?.name);
    setSelectedProductForInfo(product);
    setShowProductInfoDrawer(true);
  };

  const handleCloseProductInfo = () => {
    setShowProductInfoDrawer(false);
    setSelectedProductForInfo(null);
  };

  useEffect(() => {
    console.log('[StoreProductDetail] useEffect triggered with productId:', productId);
    const fetchStoreAndProducts = async () => {
      try {
        setLoading(true);
        setError('');

        console.log('[StoreProductDetail] Fetching product by ID:', productId);
        
        // First, fetch the specific product to get the sellerId
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        
        const productData = productDoc.data();
        const sellerId = productData.sellerId;
        
        console.log('[StoreProductDetail] Found product with sellerId:', sellerId);

        // Fetch store details - try by document ID first, then query by sellerId field
        let storeData = null;
        try {
          const storeDoc = await getDoc(doc(db, 'shops', sellerId));
          if (storeDoc.exists()) {
            storeData = { id: storeDoc.id, ...storeDoc.data() };
          }
        } catch (error) {
          console.log('[StoreProductDetail] Store not found by document ID, querying by sellerId field');
        }
        
        // If not found by ID, query by sellerId field
        if (!storeData) {
          const shopsQuery = query(
            collection(db, 'shops'),
            where('sellerId', '==', sellerId)
          );
          const shopsSnapshot = await getDocs(shopsQuery);
          if (!shopsSnapshot.empty) {
            const shopDoc = shopsSnapshot.docs[0];
            storeData = { id: shopDoc.id, ...shopDoc.data() };
          }
        }
        
        if (storeData) {
          setStore(storeData);
        }

        // Fetch all products for this store
        console.log('[StoreProductDetail] Fetching products for storeId:', sellerId);
        const productsQuery = query(
          collection(db, 'products'),
          where('sellerId', '==', sellerId),
          where('status', '==', true)
        );
        const productsSnapshot = await getDocs(productsQuery);
        console.log('[StoreProductDetail] Products query result:', {
          size: productsSnapshot.size,
          empty: productsSnapshot.empty,
          docs: productsSnapshot.docs.map(doc => ({ id: doc.id, sellerId: doc.data().sellerId, status: doc.data().status }))
        });
        const productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('[StoreProductDetail] Raw products from database:', productsData.map(p => ({ 
          id: p.id, 
          name: p.name, 
          displayOrder: p.displayOrder,
          createdAt: p.createdAt 
        })));

        // Sort products by display order first, then by creation date
        productsData.sort((a, b) => {
          // If both have display order, sort by that
          if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
            return a.displayOrder - b.displayOrder;
          }
          // If only one has display order, prioritize it
          if (a.displayOrder !== undefined) return -1;
          if (b.displayOrder !== undefined) return 1;
          // Fall back to creation date
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA; // Newest first
        });

        console.log('[StoreProductDetail] Products after sorting:', productsData.map(p => ({ 
          id: p.id, 
          name: p.name, 
          displayOrder: p.displayOrder,
          createdAt: p.createdAt 
        })));

        // Fetch review data for each product
        const productsWithReviews = await Promise.all(
          productsData.map(async (product) => {
            try {
              const reviews = await getProductReviews(product.id);
              const avgRating = reviews.length > 0 
                ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
                : 0;
              
              return {
                ...product,
                rating: avgRating,
                reviewCount: reviews.length
              };
            } catch (error) {
              console.error(`Error fetching reviews for product ${product.id}:`, error);
              return {
                ...product,
                rating: 0,
                reviewCount: 0
              };
            }
          })
        );

        setProducts(productsWithReviews);
        
        // Set current product index based on productId
        const productIndex = productsWithReviews.findIndex(p => p.id === productId);
        console.log('[StoreProductDetail] Products loaded:', productsData.map(p => ({ id: p.id, name: p.name })));
        console.log('[StoreProductDetail] Looking for productId:', productId);
        console.log('[StoreProductDetail] Found at index:', productIndex);
        console.log('[StoreProductDetail] URL params - sellerId:', sellerId, 'productId:', productId);
        console.log('[StoreProductDetail] Product IDs in array:', productsData.map(p => p.id));
        
        if (productIndex !== -1) {
          setCurrentProductIndex(productIndex);
          console.log('[StoreProductDetail] Set currentProductIndex to:', productIndex);
        } else {
          console.error('[StoreProductDetail] Product not found in array!');
          console.error('[StoreProductDetail] Available product IDs:', productsData.map(p => p.id));
          console.error('[StoreProductDetail] Looking for:', productId);
        }

        // Initialize cart animation array
        setCartAnim(Array(productsData.length).fill(false));
        
      } catch (err) {
        console.error('Error fetching store and products:', err);
        setError('Failed to load store and products');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchStoreAndProducts();
    }
  }, [productId]); // Fetch when productId changes

  // Reset initial scroll flag when productId changes
  useEffect(() => {
    setHasInitialScroll(false);
    console.log('[StoreProductDetail] Reset hasInitialScroll flag for new product:', productId);
  }, [productId]);

  // Debug: Log products state changes
  useEffect(() => {
    console.log('[StoreProductDetail] Products state updated:', products.map(p => ({ id: p.id, name: p.name, displayOrder: p.displayOrder })));
  }, [products]);

  // Debug: Log currentProductIndex changes
  useEffect(() => {
    console.log('[StoreProductDetail] Current product index changed to:', currentProductIndex);
    if (currentProductIndex !== -1 && products.length > 0) {
      const currentProduct = products[currentProductIndex];
      console.log('[StoreProductDetail] Current product:', {
        id: currentProduct?.id,
        name: currentProduct?.name,
        displayOrder: currentProduct?.displayOrder
      });
    }
  }, [currentProductIndex, products]);

  // Debug: Log hasInitialScroll changes
  useEffect(() => {
    console.log('[StoreProductDetail] hasInitialScroll changed to:', hasInitialScroll);
  }, [hasInitialScroll]);

  // Debug: Log positioning lock changes
  useEffect(() => {
    console.log('[StoreProductDetail] isInitialPositioning changed to:', isInitialPositioning);
  }, [isInitialPositioning]);

  // Fetch user's favorite products
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!currentUser) return;
      try {
        const userRef = docFirestore(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setFavoriteProducts(userSnap.data().favoriteProducts || []);
        }
      } catch (err) {
        setFavoriteProducts([]);
      }
    };
    fetchFavorites();
  }, [currentUser]);

  // Auto-play/pause logic for videos
  useEffect(() => {
    videoRefs.current.forEach((video, idx) => {
      if (video) {
        if (idx === currentProductIndex) {
          video.play().catch(err => console.log('Video play failed:', err));
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentProductIndex]);

  // Scroll to the clicked product when component loads
  useEffect(() => {
    console.log('[StoreProductDetail] Scroll effect triggered with:', { currentProductIndex, productsLength: products.length, hasInitialScroll });
    
    // Only scroll if we haven't done the initial scroll yet and we have a valid product index
    if (!hasInitialScroll && currentProductIndex !== -1 && products.length > 0) {
      // Additional check: ensure the currentProductIndex matches the productId from URL
      const expectedProduct = products[currentProductIndex];
      if (expectedProduct && expectedProduct.id === productId) {
        console.log('[StoreProductDetail] Performing initial scroll to product index:', currentProductIndex, 'for product:', expectedProduct.name);
        
        // Set positioning lock to prevent interference
        setIsInitialPositioning(true);
        console.log('[StoreProductDetail] Initial positioning lock set to true');
        
        // Temporarily remove scroll event listener to prevent interference
        const container = document.querySelector('[data-scroll-container]') || 
                         document.querySelector('.scroll-container') ||
                         document.querySelector('div[style*="overflowY: scroll"]');
        
        if (container) {
          console.log('[StoreProductDetail] Temporarily removing scroll event listener');
          container.removeEventListener('scroll', handleScroll);
        }
        
        // Use setTimeout to ensure the DOM is fully rendered
        setTimeout(() => {
          // Try multiple selectors to find the scroll container
          const container = document.querySelector('[data-scroll-container]') || 
                           document.querySelector('.scroll-container') ||
                           document.querySelector('div[style*="overflowY: scroll"]');
          
          if (container) {
            console.log('[StoreProductDetail] Found scroll container:', container);
            
            const videoHeight = window.innerWidth <= 768 ? window.innerHeight : (450 * 16 / 9);
            const scrollTop = currentProductIndex * videoHeight;
            
            console.log('[StoreProductDetail] Calculated scroll position:', {
              currentProductIndex,
              videoHeight,
              scrollTop,
              containerHeight: container.scrollHeight,
              containerScrollTop: container.scrollTop
            });
            
            // Temporarily disable scroll snap for initial positioning
            container.style.scrollSnapType = 'none';
            
            // Set scroll position
            container.scrollTop = scrollTop;
            
            // Verify the scroll position was set correctly
            setTimeout(() => {
              const actualScrollTop = container.scrollTop;
              console.log('[StoreProductDetail] Scroll position verification:', {
                expected: scrollTop,
                actual: actualScrollTop,
                difference: Math.abs(scrollTop - actualScrollTop)
              });
              
              if (Math.abs(scrollTop - actualScrollTop) > 10) {
                console.warn('[StoreProductDetail] Scroll position not set correctly, retrying...');
                container.scrollTop = scrollTop;
              }
            }, 50);
            
            // Re-enable scroll snap after positioning
            setTimeout(() => {
              container.style.scrollSnapType = 'y mandatory';
              console.log('[StoreProductDetail] Scroll snap re-enabled after delay');
              
              // Re-add scroll event listener
              container.addEventListener('scroll', handleScroll);
              console.log('[StoreProductDetail] Scroll event listener re-added');
              
              // Release positioning lock and mark initial scroll complete
              setIsInitialPositioning(false);
              setHasInitialScroll(true);
              console.log('[StoreProductDetail] Initial positioning lock released, initial scroll marked complete');
            }, 1000); // Increased delay to ensure positioning is stable
            
            console.log('[StoreProductDetail] Initial scroll completed to position:', scrollTop, 'for product index:', currentProductIndex);
          } else {
            console.error('[StoreProductDetail] Scroll container not found!');
            
            // Fallback: try to scroll the product into view using scrollIntoView
            setTimeout(() => {
              const productElement = document.querySelector(`[data-product-index="${currentProductIndex}"]`);
              if (productElement) {
                console.log('[StoreProductDetail] Using fallback scrollIntoView for product index:', currentProductIndex);
                productElement.scrollIntoView({ behavior: 'auto', block: 'start' });
                
                // Mark that we've done the initial scroll
                setTimeout(() => setHasInitialScroll(true), 500);
              } else {
                console.error('[StoreProductDetail] Product element not found for fallback scroll');
                
                // Last resort: try window.scrollTo
                const videoHeight = window.innerWidth <= 768 ? window.innerHeight : (450 * 16 / 9);
                const scrollTop = currentProductIndex * videoHeight;
                console.log('[StoreProductDetail] Trying window.scrollTo to:', scrollTop);
                window.scrollTo(0, scrollTop);
                
                // Mark that we've done the initial scroll
                setTimeout(() => setHasInitialScroll(true), 500);
              }
            }, 300);
          }
        }, 200); // Increased timeout to ensure DOM is ready
      } else {
        console.log('[StoreProductDetail] Skipping scroll - product mismatch:', {
          expectedProductId: expectedProduct?.id,
          urlProductId: productId,
          currentProductIndex
        });
      }
    } else if (hasInitialScroll) {
      console.log('[StoreProductDetail] Skipping scroll - initial scroll already completed');
    } else {
      console.log('[StoreProductDetail] Skipping scroll - conditions not met:', {
        hasInitialScroll,
        currentProductIndex,
        productsLength: products.length
      });
    }
  }, [currentProductIndex, products.length, hasInitialScroll, productId]);

  // Auto-open sheet if autoOpen parameter is present (only once on initial load)
  useEffect(() => {
    const autoOpen = searchParams.get('autoOpen');
    console.log('[StoreProductDetail] Auto-open check:', {
      autoOpen,
      productsLength: products.length,
      currentProductIndex,
      hasAutoOpened,
      currentProduct: products[currentProductIndex] ? {
        id: products[currentProductIndex].id,
        name: products[currentProductIndex].name,
        variants: products[currentProductIndex].variants
      } : null
    });
    
    // Only auto-open once when the page first loads, not on every scroll
    if (autoOpen === 'true' && products.length > 0 && currentProductIndex !== -1 && !hasAutoOpened) {
      console.log('[StoreProductDetail] Auto-opening sheet for product with variants (first time only)');
      const currentProduct = products[currentProductIndex];
      if (currentProduct && currentProduct.variants && currentProduct.variants.length > 0) {
        console.log('[StoreProductDetail] Opening sheet for product:', currentProduct.name);
        openSheet(currentProductIndex);
        setHasAutoOpened(true); // Mark as auto-opened to prevent future auto-opens
      } else {
        console.log('[StoreProductDetail] Product has no variants, not opening sheet');
        setHasAutoOpened(true); // Mark as processed even if no variants
      }
    }
  }, [searchParams, products.length, currentProductIndex, hasAutoOpened]);

  const handleScroll = (e) => {
    // Don't handle scroll events until initial positioning is complete
    if (!hasInitialScroll || isInitialPositioning) {
      console.log('[StoreProductDetail] Skipping scroll handler - initial positioning not complete or in progress:', {
        hasInitialScroll,
        isInitialPositioning
      });
      return;
    }
    
    const scrollTop = e.target.scrollTop;
    // Use responsive height calculation: full viewport on mobile, aspect ratio on web
    const videoHeight = window.innerWidth <= 768 ? window.innerHeight : (450 * 16 / 9);
    const idx = Math.round(scrollTop / videoHeight);
    
    console.log('[StoreProductDetail] Scroll handler - scrollTop:', scrollTop, 'calculated index:', idx, 'current index:', currentProductIndex);
    
    if (idx !== currentProductIndex && idx >= 0 && idx < products.length) {
      console.log('[StoreProductDetail] Updating currentProductIndex from', currentProductIndex, 'to', idx);
      setCurrentProductIndex(idx);
    }
  };

  // Add to cart handler with animation
  const handleAddToCart = async (product, idx) => {
    try {
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image || product.imageUrl || '/default-product.png',
        qty: 1,
        sellerId: product.sellerId || store?.sellerId || store?.id || null,
      };
      
      await addCartItem(cartItem);
      
      // Frontend: Trigger +1 animation
      setCartAnim(anim => {
        const next = [...anim];
        next[idx] = true;
        return next;
      });
      setTimeout(() => {
        setCartAnim(anim => {
          const next = [...anim];
          next[idx] = false;
          return next;
        });
      }, 900);
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  // Add to cart with variations
  const handleAddToCartWithVariations = async (product, idx) => {
    try {
      // Create unique ID that includes variant information
      const variantString = Object.keys(variationSelections).length > 0 
        ? Object.entries(variationSelections)
            .map(([key, value]) => `${key}:${value}`)
            .sort()
            .join('|')
        : '';
      const uniqueId = variantString ? `${product.id}_${variantString}` : product.id;
      
      const cartItem = {
        id: uniqueId, // Use unique ID that includes variants
        productId: product.id, // Keep original product ID for reference
        name: product.name,
        price: product.price,
        image: product.image || product.imageUrl || '/default-product.png',
        qty: quantity,
        variant: Object.keys(variationSelections).length > 0 ? variationSelections : null, // Store variants as a nested object
        sellerId: product.sellerId || store?.sellerId || store?.id || null,
      };
      
      await addCartItem(cartItem);
      
      setCartAnim(anim => {
        const next = [...anim];
        next[idx] = true;
        return next;
      });
      setTimeout(() => {
        setCartAnim(anim => {
          const next = [...anim];
          next[idx] = false;
          return next;
        });
      }, 900);
      closeSheet();
    } catch (err) {
      console.error('Error adding to cart with variations:', err);
    }
  };

  // Favorite/unfavorite logic
  const toggleFavorite = async (productId, isFav) => {
    if (!currentUser) return;
    setFavoriteProducts(favs =>
      isFav ? favs.filter(id => id !== productId) : [...favs, productId]
    );
    const userRef = docFirestore(db, 'users', currentUser.uid);
    try {
      if (isFav) {
        await updateDoc(userRef, { favoriteProducts: arrayRemove(productId) });
      } else {
        await updateDoc(userRef, { favoriteProducts: arrayUnion(productId) });
      }
    } catch (err) {
      console.error('Error updating favorites:', err);
      // Revert optimistic update
      setFavoriteProducts(favs =>
        isFav ? [...favs, productId] : favs.filter(id => id !== productId)
      );
    }
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartY.current - touchEndY.current;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && currentProductIndex < products.length - 1) {
        // Swipe up - next product
        setCurrentProductIndex(prev => prev + 1);
      } else if (diff < 0 && currentProductIndex > 0) {
        // Swipe down - previous product
        setCurrentProductIndex(prev => prev - 1);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8f5f2'
      }}>
        <div>Loading products...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8f5f2'
      }}>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8f5f2'
      }}>
        <div>No products found in this store</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', background: '#f8f5f2' }}>
      {/* Available Chip: Positioned relative to video container */}
      <AvailableChip 
        product={products[currentProductIndex] || products[0]} 
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 1000,
        }}
      />

      {/* Back Button: Only show if viewing a single product */}
      <button
        onClick={() => navigate(-1)}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 1000,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-label="Go back"
      >
        <ChevronLeft set="regular" size={24} primaryColor="#fff" />
      </button>

      {/* Product Display Container */}
      <div 
        className="scroll-container"
        style={{
          height: '100vh',
          height: 'calc(var(--vh, 1vh) * 100)',
          height: '100dvh',
          overflowY: 'scroll',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch', // Better iOS scrolling
          overscrollBehavior: 'contain', // Prevent overscroll
        }}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-scroll-container // Added data attribute for scrolling
      >
        {products.map((product, idx) => (
          <div
            key={product.id}
            data-product-index={idx}
            style={{
              height: '100vh',
              height: 'calc(var(--vh, 1vh) * 100)',
              height: '100dvh',
              width: '100%',
              position: 'relative',
              scrollSnapAlign: 'start',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Product Media Container */}
            <div 
              className="product-media-container"
              style={{
                width: '100%',
                height: '100vh',
                height: 'calc(var(--vh, 1vh) * 100)',
                height: '100dvh',
                maxHeight: '100vh',
                maxHeight: 'calc(var(--vh, 1vh) * 100)',
                maxHeight: '100dvh',
                aspectRatio: '9/16',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '12px',
                margin: '0 auto',
              }}>
              {product.videoUrl ? (
                <video
                  ref={el => (videoRefs.current[idx] = el)}
                  src={product.videoUrl}
                  muted
                  autoPlay
                  style={{
                    objectFit: 'contain', // Changed from cover to contain to prevent cut-off
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                  loop
                  playsInline
                />
              ) : (
                <img
                  src={product.image || product.imageUrl || '/default-product.png'}
                  alt={product.name}
                  style={{
                    objectFit: 'contain', // Changed from cover to contain to prevent cut-off
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                />
              )}
            </div>

            {/* Product Info Overlay */}
            <div
              className="product-info-overlay"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 'calc(72px + env(safe-area-inset-bottom) + 20px)', // Much closer to bottom of video
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                width: '100%',
                padding: '0',
                boxSizing: 'border-box',
                pointerEvents: 'none',
                // Center the content over the video instead of spreading to edges
                maxWidth: '450px', // Match video container width
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {/* Text block, positioned over video */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenProductInfo(product);
                }}
                style={{
                  color: '#fff',
                  padding: '0 1.5rem 0 1.5rem',
                  maxWidth: '60%', // Reduced from 70vw to fit better over video
                  textShadow: '0 2px 8px #000a',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '1.5rem' }}>
                  {product.name || 'Unnamed Product'} <span style={{ fontWeight: 400, fontSize: '1.1rem' }}>${product.price || '0.00'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0.25rem 0' }}>
                  {product.rating && typeof product.rating === 'number' && !isNaN(product.rating) ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenReviews(product);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      {renderStarRating(product.rating)}
                      <span style={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }}>
                        ({product.reviewCount || 0})
                      </span>
                    </button>
                  ) : (
                    <span style={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }}>
                      No reviews yet
                    </span>
                  )}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  margin: '0.25rem 0 0.5rem 0',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.4'
                }}>
                  {product.description || 'Delicious food from a local creator.'}
                </div>
              </div>

              {/* Icon column, positioned over video */}
              <div
                className="action-icons"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 24,
                  alignItems: 'center',
                  marginRight: '1.5rem', // Reduced from 16px to 1.5rem for consistency
                  marginBottom: 8,
                  pointerEvents: 'auto',
                }}
              >
                {/* Store Icon */}
                <button
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    border: '2px solid #fff',
                    padding: 0,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    background: 'transparent',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                  aria-label="Store profile"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/storefront/${store?.id || 'unknown'}`);
                  }}
                >
                  <img 
                    src={store?.coverPhotoURL || store?.coverphotoURL || store?.coverPhoto || store?.profileImage || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face'} 
                    alt={`${store?.name || 'Store'} profile`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain', // Changed from cover to contain to prevent cut-off
                      borderRadius: 10,
                    }}
                  />
                </button>

                {/* Cart Icon */}
                <div style={{ position: 'relative', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 12,
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 48,
                      minHeight: 48
                    }}
                    aria-label="Add to cart"
                    onClick={(e) => {
                      e.stopPropagation();
                      const hasVariations = checkProductHasVariations(product);
                      if (hasVariations) {
                        openSheet(idx);
                      } else {
                        handleAddToCart(product, idx);
                      }
                    }}
                  >
                    <Buy set="regular" size={24} primaryColor="#fff" />
                  </button>
                  {/* +1 Animation */}
                  {cartAnim[idx] && (
                    <span
                      style={{
                        position: 'absolute',
                        right: 8,
                        bottom: 12,
                        color: '#C97B53',
                        fontWeight: 700,
                        fontSize: 20,
                        background: 'transparent',
                        borderRadius: 12,
                        padding: '2px 10px',
                        pointerEvents: 'none',
                        opacity: 1,
                        transform: 'translateY(0)',
                        animation: 'fadeUp 1.7s ease-out forwards',
                        zIndex: 2,
                      }}
                    >
                      +1
                    </span>
                  )}
                </div>

                {/* Heart Icon */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(product.id, favoriteProducts.includes(product.id));
                  }}
                  aria-label={favoriteProducts.includes(product.id) ? 'Unfavorite' : 'Favorite'}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 12,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 48,
                    minHeight: 48
                  }}
                >
                  <Heart
                    set={favoriteProducts.includes(product.id) ? 'bold' : 'light'}
                    primaryColor={favoriteProducts.includes(product.id) ? '#F44336' : '#fff'}
                    size={24}
                  />
                </button>

                {/* More Options Icon */}
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 12,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 48,
                    minHeight: 48
                  }}
                  aria-label="More options"
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    handleOpenProductInfo(products[idx]);
                  }}
                >
                  <MoreCircle set="regular" size={24} primaryColor="#fff" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* +1 Animation Keyframes */}
        <style>{`
          @keyframes fadeUp {
            0% { opacity: 1; transform: translateY(0); }
            60% { opacity: 1; transform: translateY(-24px); }
            100% { opacity: 0; transform: translateY(-48px); }
          }
        `}</style>

        {/* Bottom Sheet */}
        {showSheet && sheetProductIdx !== null && (
          <>
            {/* Overlay */}
            <div
              onClick={closeSheet}
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.6)',
                zIndex: 1000,
                transition: 'opacity 0.3s',
              }}
              aria-label="Close more info layover"
            />
            {/* Bottom Sheet */}
            <div
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1001,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(8px)',
                borderTopLeftRadius: window.innerWidth <= 768 ? 0 : 24,
                borderTopRightRadius: window.innerWidth <= 768 ? 0 : 24,
                minHeight: window.innerWidth <= 768 ? '60vh' : '40vh',
                maxHeight: window.innerWidth <= 768 ? '90vh' : '80vh',
                boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
                padding: window.innerWidth <= 768 ? '24px 16px 20px 16px' : '32px 20px 24px 20px',
                animation: 'slideUpSheet 0.3s cubic-bezier(.4,1.6,.6,1)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: window.innerWidth <= 768 ? 16 : 20,
              }}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
            >
              <div style={{ width: 40, height: 4, background: '#eee', borderRadius: 2, alignSelf: 'center', marginBottom: 12 }} />
              <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0 }}>
                {products[sheetProductIdx].name}
              </h2>
              <div style={{ 
                background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 600, 
                fontSize: 18 
              }}>
                ${products[sheetProductIdx].price}
              </div>

              {/* Description */}
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Description</div>
                <div style={{ fontSize: 15, color: '#444' }}>
                  {products[sheetProductIdx].description || 'Delicious food from a local creator.'}
                </div>
              </div>

              {/* Allergens */}
              {products[sheetProductIdx].allergens && products[sheetProductIdx].allergens.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Allergens</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {products[sheetProductIdx].allergens.map((allergen, i) => (
                      <span key={i} style={{ 
                        background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                        backgroundSize: '200% 200%',
                        animation: 'gradientShift 3s ease infinite',
                        color: '#fff', 
                        borderRadius: 8, 
                        padding: '4px 10px', 
                        fontSize: 14 
                      }}>
                        {allergen}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Variations */}
              {products[sheetProductIdx].variants && products[sheetProductIdx].variants.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Select Options</div>
                  {products[sheetProductIdx].variants.map((variant, i) => {
                    // Add error handling for variant structure
                    if (!variant || typeof variant !== 'object') {
                      console.error('[StoreProductDetail] Invalid variant:', variant);
                      return null;
                    }
                    
                    const variantKey = variant.key || variant.label || `variant-${i}`;
                    const variantLabel = variant.label || variant.key || `Option ${i + 1}`;
                    const variantOptions = Array.isArray(variant.options) ? variant.options : [];
                    
                    return (
                      <div key={variantKey} style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, color: '#333' }}>
                          {variantLabel}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {variantOptions.map((option, j) => {
                            // Add error handling for option structure
                            if (!option || typeof option !== 'object') {
                              console.error('[StoreProductDetail] Invalid option:', option);
                              return null;
                            }
                            
                            const optionKey = option.id || option.label || `option-${j}`;
                            const optionLabel = option.label || option.name || `Option ${j + 1}`;
                            
                            return (
                              <button
                                key={optionKey}
                                type="button"
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: 20,
                                  border: variationSelections[variantKey] === option ? '2px solid #e76f51' : '1px solid #ddd',
                                  background: variationSelections[variantKey] === option 
                                    ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)'
                                    : '#fff',
                                  backgroundSize: variationSelections[variantKey] === option ? '200% 200%' : 'auto',
                                  animation: variationSelections[variantKey] === option ? 'gradientShift 3s ease infinite' : 'none',
                                  color: variationSelections[variantKey] === option ? '#fff' : '#222',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  outline: 'none',
                                  fontSize: 15,
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={() => {
                                  setVariationSelections(sel => ({ ...sel, [variantKey]: option }));
                                }}
                                aria-label={`Select ${optionLabel} for ${variantLabel}`}
                              >
                                {optionLabel}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quantity Selector */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Quantity</div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12
                }}>
                  <button
                    type="button"
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid #e76f51',
                      background: 'white',
                      color: '#e76f51',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <span style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    minWidth: '40px',
                    textAlign: 'center'
                  }}>
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(prev => prev + 1)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid #e76f51',
                      background: '#e76f51',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                style={{
                  marginTop: 16,
                  background: allVariationsSelected(products[sheetProductIdx]) 
                    ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)'
                    : '#E5E5E5',
                  backgroundSize: allVariationsSelected(products[sheetProductIdx]) ? '200% 200%' : 'auto',
                  animation: allVariationsSelected(products[sheetProductIdx]) ? 'gradientShift 3s ease infinite' : 'none',
                  color: allVariationsSelected(products[sheetProductIdx]) ? '#fff' : '#aaa',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 0',
                  fontSize: 18,
                  fontWeight: '700',
                  cursor: allVariationsSelected(products[sheetProductIdx]) ? 'pointer' : 'not-allowed',
                  width: '100%',
                  boxShadow: '0 2px 8px #0001',
                  transition: 'all 0.2s',
                }}
                onClick={() => {
                  if (allVariationsSelected(products[sheetProductIdx])) {
                    handleAddToCartWithVariations(products[sheetProductIdx], sheetProductIdx);
                  }
                }}
                aria-label="Add to cart from layover"
                disabled={!allVariationsSelected(products[sheetProductIdx])}
              >
                Add to Cart
              </button>
            </div>
            <style>{`
              @keyframes slideUpSheet {
                0% { transform: translateY(100%); }
                100% { transform: translateY(0); }
              }
            `}</style>
          </>
        )}
      </div>

      {/* Product Reviews Drawer */}
      <ProductReviewsDrawer
        isOpen={showReviewsDrawer}
        onClose={handleCloseReviews}
        product={selectedProductForReviews}
        productRating={selectedProductForReviews?.rating || 0}
        productReviewCount={selectedProductForReviews?.reviewCount || 0}
      />

      {/* Product Info Drawer */}
      <ProductInfoDrawer
        isOpen={showProductInfoDrawer}
        onClose={handleCloseProductInfo}
        product={selectedProductForInfo}
        onAddToCart={async (product, quantity, variants, fulfillmentData) => {
          try {
            console.log('[StoreProductDetail] Adding to cart from drawer:', { 
              product, 
              quantity, 
              variants, 
              fulfillmentData 
            });
            
            // Create unique ID that includes variant information
            const variantString = Object.keys(variants || {}).length > 0 
              ? Object.entries(variants)
                  .map(([key, value]) => `${key}:${value}`)
                  .sort()
                  .join('|')
              : '';
            const uniqueId = variantString ? `${product.id}_${variantString}` : product.id;
            
            const cartItem = {
              id: uniqueId,
              productId: product.id,
              name: product.name,
              price: product.price,
              image: product.image || product.imageUrl || '/default-product.png',
              qty: quantity,
              variant: Object.keys(variants || {}).length > 0 ? variants : null,
              fulfillmentType: fulfillmentData?.fulfillmentType || 'pickup',
              pickupDate: fulfillmentData?.pickupDate || '',
              readyTime: fulfillmentData?.readyTime || '',
              sellerId: product.sellerId || store?.sellerId || store?.id || null,
            };
            
            await addCartItem(cartItem);
            console.log('[StoreProductDetail] Successfully added to cart:', cartItem);
            
            // Close the drawer after successful add
            handleCloseProductInfo();
            
          } catch (err) {
            console.error('[StoreProductDetail] Error adding to cart from drawer:', err);
          }
        }}
        onToggleFavorite={async (product) => {
          try {
            console.log('[StoreProductDetail] Toggling favorite from drawer:', product);
            await toggleFavorite(product.id, !favoriteProducts.includes(product.id));
          } catch (err) {
            console.error('[StoreProductDetail] Error toggling favorite from drawer:', err);
          }
        }}
        isFavorite={selectedProductForInfo ? favoriteProducts.includes(selectedProductForInfo.id) : false}
        cartQuantity={selectedProductForInfo ? getCartQty(selectedProductForInfo.id) : 0}
      />

      <BottomNavigation />
    </div>
  );
};

export default StoreProductDetail;